import {
  Checkbox,
  CommandBar,
  ContextualMenu,
  ContextualMenuItem,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IButtonProps,
  ICommandBarItemProps,
  IContextualMenuItemProps,
  Link,
  Persona,
  PersonaSize,
  PrimaryButton,
} from "@fluentui/react";
import {
  MgtPerson,
  PeoplePicker,
  Person,
  PersonCardInteraction,
  PersonType,
  UserType,
  ViewType,
} from "@microsoft/mgt-react";
import { useBoolean, useId } from "@fluentui/react-hooks";
import React, { useReducer, useState } from "react";
import { directoryObject } from "../objects/directoryObj";
import { toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../authConfig";
import { getUserByEmail } from "../services/graph";
import Swal from "sweetalert2";
import { renderToStaticMarkup } from "react-dom/server";
import { setAllCheckboxesByClass } from "../utils";

interface OwnerManagerProps {
  currentOwners: string[];
  currentNonValidOwners: string[];
  readOnlyOwners: string[];
  currentNonExistingOwners: string[];
  resourceId: string;
  siteId: string;
  addFunction: (ownerList: directoryObject[], resourceId: string) => void;
  removeFunction: (ownerList: directoryObject[], resourceId: string) => void;
  isM365: boolean;
  isAGS: boolean;
}

const CustomMenuItem: React.FunctionComponent<IContextualMenuItemProps> = (
  props,
) => {
  return <ContextualMenuItem {...props} />;
};

const dialogStyles = { main: { maxWidth: 450 } };
const dragOptions = {
  moveMenuItemText: "Move",
  closeMenuItemText: "Close",
  menu: ContextualMenu,
  keepInBounds: true,
};

export function OwnerManager(props: OwnerManagerProps) {
  class ownerObj {
    email!: string;
    isValid!: boolean;
  }
  const allOwners: ownerObj[] = [];
  props.currentOwners.map((o) => allOwners.push({ email: o, isValid: true }));

  try {
    props.currentNonValidOwners.map((o) =>
      allOwners.push({ email: o, isValid: false }),
    );
  } catch {
    props.currentNonValidOwners = [];
  }

  if (typeof props.currentNonExistingOwners === "undefined") {
    props.currentNonExistingOwners = [];
  }

  const { instance, accounts } = useMsal();
  MgtPerson.config.useContactApis = true;
  let selectedOwners: Array<directoryObject> = [];
  const [hidePickerErrorSummary, setHidePickerErrorSummary] = useState(true);
  const [checkedOwners, setCheckedOwners] = useState<string[]>([]);
  const [errorSummaryText, setErrorSummaryText] = useState("");
  const [showRemovalSteps, setShowRemovalSteps] = useState(false);
  const [hideOwnerSelectionDialog, { toggle: toggleOwnerSelectionHideDialog }] =
    useBoolean(true);
  const [isDraggable] = useBoolean(false);
  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const modalProps = React.useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: false,
      styles: dialogStyles,
      dragOptions: isDraggable ? dragOptions : undefined,
    }),
    [isDraggable, labelId, subTextId],
  );
  const ownerDialogContentProps = {
    type: DialogType.normal,
    title: `Add ${props.isM365 ? "owners" : "site collection administrators"}`,
    closeButtonAriaLabel: "Close",
    subText: `Manage ${props.isM365 ? "owners" : "site administrators"} for this workspace`,
  };
  function handleOwnerAddDialog() {
    setHidePickerErrorSummary(true);
    setErrorSummaryText("");
    toggleOwnerSelectionHideDialog();
  }
  function renderOwnerRemovalWarning(ownerList: string[]): JSX.Element {
    return (
      <div>
        <h5>Remove the selected guest(s)?</h5>
        <div
          style={{
            display: ownerList.includes((window as any).CURRENT_USER_EMAIL)
              ? "block"
              : "none",
          }}
        >
          <br></br>
          <div className="alert alert-warning" role="alert">
            <strong>Warning: </strong> You are attempting to remove yourself;
            you will no longer be able to manage this workspace.
          </div>
        </div>
      </div>
    );
  }
  function handleOwnerCheckColumn(ownerId: string, isChecked: boolean) {
    if (isChecked) {
      if (checkedOwners.includes(ownerId) === false) {
        setCheckedOwners([...checkedOwners, ownerId]);
      }
    } else {
      setCheckedOwners(
        checkedOwners.filter((email) => {
          return email !== ownerId;
        }),
      );
    }
  }

  function handleOwnerRemovalDialog() {
    let selectedOwners: directoryObject[] = [];
    if (checkedOwners.length > 0) {
      let nonValidOwnersCount = 0;

      /*This code block calculates how many owners will result after the removal
      To allow the removal, a minimum of 2 owners is needed
      Non valid accounts are not considered for this calculation
      Example:
      We want to remove 2 owners, but one is a system account
      The Resource has 3 owners in total
      Resulting Owners = (Current Owners) - ((Selected owners)-(Non valid owners))
      3 - (2-1) = 2
      */
      for (let index = 0; index < props.currentNonValidOwners.length; index++) {
        if (
          checkedOwners.find(
            (element) => element === props.currentNonValidOwners[index],
          )
        ) {
          nonValidOwnersCount++;
        }
      }
      let ownersAfterRemoval =
        props.currentOwners.length -
        (checkedOwners.length - nonValidOwnersCount);
      if (props.currentOwners.length >= 2 && ownersAfterRemoval >= 2) {
        Swal.fire({
          html: renderToStaticMarkup(renderOwnerRemovalWarning(checkedOwners)),
          showCancelButton: true,
          confirmButtonColor: "#CE0000",
          confirmButtonText: "Remove",
        }).then((result) => {
          if (result.isConfirmed) {
            const accessTokenRequest = {
              scopes: apiScopes.graphScopes,
              account: accounts[0],
            };
            instance
              .acquireTokenSilent(accessTokenRequest)
              .then((accessTokenResponse) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                // Map over the array and call the async function for each element
                const promises = checkedOwners.map(async (ownerEmail) => {
                  try {
                    let userIdResponse = await getUserByEmail(
                      accessToken,
                      ownerEmail,
                    );
                    if ((userIdResponse as any).id !== "") {
                      let currentUserObj: directoryObject = {};
                      currentUserObj.userPrincipalName = ownerEmail;
                      currentUserObj.id = (userIdResponse as any).id;
                      selectedOwners.push(currentUserObj);
                    }
                  } catch {
                    console.error(
                      `Error while retrieving ID for ${ownerEmail}`,
                    );
                  }
                });
                Promise.all(promises).then(async () => {
                  setAllCheckboxesByClass(
                    `chk-owners-${props.isM365 ? "M365" : "SPO"}-${props.isM365 ? props.resourceId : props.siteId}`,
                    false,
                  );
                  await props.removeFunction(selectedOwners, props.resourceId);
                });
              });
          }
        });
      } else {
        Swal.fire({
          title: "Ownership Enforcement",
          text: `The workspace must have at least 2 ${props.isM365 ? "owners" : "administrators"}`,
        });
      }
    }
  }
  function searchEmail(emailParam: string): boolean {
    for (let index = 0; index < props.currentOwners.length; index++) {
      let currentOwner = props.currentOwners[index];
      if (
        currentOwner.trim().toLowerCase() === emailParam.trim().toLowerCase()
      ) {
        return true;
      }
    }
    return false;
  }
  const _items: ICommandBarItemProps[] = [
    {
      key: "add",
      text: `Add ${props.isM365 ? "Owners" : "Admins"}`,
      iconProps: { iconName: "Add" },
      onClick: () => {
        handleOwnerAddDialog();
      },
      disabled: props.isAGS,
    },
    {
      key: "remove",
      text: "Remove",
      iconProps: { iconName: "Cancel" },
      onClick: () => {
        handleOwnerRemovalDialog();
      },
      disabled: props.isAGS,
    },
    {
      key: "refresh",
      text: "Refresh",
      iconProps: { iconName: "Refresh" },
      onClick: () => {
        Swal.showLoading();
        const randomNum: number = Math.floor(Math.random() * (5 - 2 + 1)) + 2;
        setTimeout(() => {
          Swal.close();
        }, randomNum * 1000);
      },
    },
    {
      key: "feedback",
      text: "Feedback",
      iconProps: { iconName: "Feedback" },
      onClick: () => {
        const feedbackLinkElement = document.getElementById(
          "btnOpenFeedbackForm",
        );
        feedbackLinkElement?.click();
      },
    },
  ];
  const overflowProps: IButtonProps = {
    menuProps: {
      contextualMenuItemAs: CustomMenuItem,
      items: [],
      isBeakVisible: true,
      beakWidth: 20,
    },
  };
  return (
    <div>
      <Dialog
        hidden={hideOwnerSelectionDialog}
        onDismiss={toggleOwnerSelectionHideDialog}
        dialogContentProps={ownerDialogContentProps}
        modalProps={modalProps}
      >
        <div className="row">
          <div className="col">
            <div>
              <PeoplePicker
                type={PersonType.person}
                userType={UserType.user}
                className="mgt-light light-border"
                userFilters={`userType eq 'Member' and mail ne '${
                  (window as any).CURRENT_USER_EMAIL
                }'`}
                showMax={10}
                selectionMode="multiple"
                selectionChanged={(event: any) => {
                  selectedOwners = [];
                  let selectedValue: Array<directoryObject> = event[
                    "detail"
                  ] as Array<directoryObject>;
                  if (selectedValue.length > 0) {
                    selectedOwners = selectedValue;
                    setHidePickerErrorSummary(true);
                    setErrorSummaryText("");
                  } else {
                    setErrorSummaryText(
                      `No ${props.isM365 ? "owner" : "site administrator"} was selected`,
                    );
                    setHidePickerErrorSummary(false);
                  }
                }}
              ></PeoplePicker>
              <div
                className="invalid-feedback"
                style={{ display: hidePickerErrorSummary ? "none" : "grid" }}
              >
                {errorSummaryText}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <PrimaryButton
            onClick={async () => {
              let filteredOwners: directoryObject[] = [];
              if (selectedOwners.length > 0) {
                selectedOwners.forEach((currOwner) => {
                  if (
                    searchEmail(
                      currOwner.userPrincipalName
                        ?.trim()
                        .toLowerCase() as string,
                    ) === false
                  ) {
                    filteredOwners.push(currOwner);
                  }
                });
                if (filteredOwners.length > 0) {
                  toggleOwnerSelectionHideDialog();
                  setAllCheckboxesByClass(
                    `chk-owners-${props.isM365 ? "M365" : "SPO"}-${props.isM365 ? props.resourceId : props.siteId}`,
                    false,
                  );
                  forceUpdate();
                  await props.addFunction(filteredOwners, props.resourceId);
                } else {
                  toast.info(
                    `The selected users are already ${props.isM365 ? "owners" : "site administrators"}`,
                    {
                      type: "info",
                      isLoading: false,
                      autoClose: 8000,
                    },
                  );
                }
              }
            }}
            text="Confirm"
          />
          <DefaultButton
            onClick={() => {
              setHidePickerErrorSummary(true);
              setErrorSummaryText("");
              toggleOwnerSelectionHideDialog();
            }}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>
      <div>
        <br />
        <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.isAGS ? "grid" : "none",
          }}
        >
          <div className="alert alert-warning" role="alert">
            {" "}
            <strong>⚠</strong>
            This site is managed by the AGS system, go{" "}
            <a
              href="https://ags.intel.com/identityiq/home.jsf"
              target="_blank"
              className="alert-link"
              rel="noopener noreferrer"
            >
              HERE
            </a>{" "}
            if you require to manage ownership of this space
          </div>
        </div>
        <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.currentNonValidOwners.length > 0 ? "grid" : "none",
          }}
        >
          <div className="alert alert-light" role="alert">
            Your workspace includes{" "}
            <span className={"badge rounded-pill generic-user-pill"}>
              {"System/Generic"}
            </span>{" "}
            accounts designated as owners. While these accounts are valid, they
            are not considered when calculating ownership compliance.
          </div>
        </div>
        <div
          style={{
            paddingLeft: "14px",
            paddingRight: "14px",
            display: props.currentOwners.length > 0 ? "none" : "grid",
          }}
        >
          <div className="alert alert-light" role="alert">
            Owners / Site Collection Administrators are still being loaded,
            please wait
          </div>
        </div>

        {/* ── Deleted-account notice ── */}
        {props.currentNonExistingOwners.length > 0 && (
          <div className="px-3 mb-3">
            <div className="card border-warning shadow-sm">
              {/* Card header */}
              <div className="card-header bg-warning bg-opacity-25 d-flex align-items-center gap-2 py-2">
                <span style={{ fontSize: "1.15rem" }}>⚠️</span>
                <strong className="text-warning-emphasis">
                  {props.currentNonExistingOwners.length === 1
                    ? "1 account is"
                    : `${props.currentNonExistingOwners.length} accounts are`}{" "}
                  listed as{" "}
                  {props.isM365 ? "owners" : "site collection administrators"}{" "}
                  but no longer exist in the directory
                </strong>
              </div>

              <div className="card-body pb-2">
                {/* Explanation */}
                <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                  The account(s) below were previously assigned as{" "}
                  <strong>
                    {props.isM365 ? "owners" : "site collection administrators"}
                  </strong>{" "}
                  but have since been deleted from your organization's
                  directory. SharePoint still holds a reference to them, which
                  is why they appear here. These accounts{" "}
                  <strong>cannot be managed through this portal</strong> and
                  must be cleaned up directly in SharePoint.
                </p>

                {/* Accounts list */}
                <div className="mb-3">
                  <table className="table table-sm table-bordered align-middle mb-0">
                    <thead>
                      <tr className="table-warning">
                        <th
                          className="text-start ps-3"
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            width: "60%",
                          }}
                        >
                          Account
                        </th>
                        <th
                          className="text-center"
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            width: "15%",
                          }}
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {props.currentNonExistingOwners.map((owner, idx) => (
                        <tr
                          key={idx}
                          style={{
                            backgroundColor:
                              idx % 2 === 0 ? "#fffdf0" : "#fafafa",
                          }}
                        >
                          <td className="ps-3">
                            <Persona
                              text={owner}
                              secondaryText="Account deleted from directory"
                              size={PersonaSize.size32}
                            />
                          </td>
                          <td className="text-center">
                            <span className="badge bg-danger text-nowrap">
                              Deleted from directory
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <br></br>

                {/* Accordion — how to remove */}
                <div
                  className="accordion accordion-flush"
                  id="removalStepsAccordion"
                >
                  <div className="accordion-item border rounded">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button fw-semibold py-2 ${showRemovalSteps ? "" : "collapsed"}`}
                        style={{
                          fontSize: "0.875rem",
                          backgroundColor: showRemovalSteps
                            ? "#fff8e1"
                            : "#fffdf0",
                        }}
                        type="button"
                        onClick={() => setShowRemovalSteps((prev) => !prev)}
                      >
                        🛠️ How to remove these accounts from SharePoint
                      </button>
                    </h2>
                    <div
                      className={`accordion-collapse collapse ${showRemovalSteps ? "show" : ""}`}
                    >
                      <div
                        className="accordion-body"
                        style={{
                          fontSize: "0.875rem",
                          backgroundColor: "#fffdf0",
                        }}
                      >
                        <p className="mb-2">
                          You will need <strong>SharePoint Admin</strong> or{" "}
                          <strong>Site Collection Admin</strong> access to
                          complete these steps:
                        </p>
                        <div className="d-flex flex-column gap-2 mb-3">
                          {[
                            {
                              step: 1,
                              title: "Open Site Settings",
                              description: (
                                <>
                                  Navigate to the affected site in SharePoint,
                                  click the <strong>gear icon ⚙️</strong> on the
                                  top right and select{" "}
                                  <strong>Site Settings</strong> from the
                                  dropdown
                                </>
                              ),
                            },
                            {
                              step: 2,
                              title: "Go to Site Collection Administrators",
                              description: (
                                <>
                                  Under the{" "}
                                  <strong>Users and Permissions</strong>{" "}
                                  section, click{" "}
                                  <strong>
                                    Site Collection Administrators
                                  </strong>
                                </>
                              ),
                            },
                            {
                              step: 3,
                              title: "Remove the deleted account(s)",
                              description: (
                                <>
                                  Locate the deleted account(s) listed above in
                                  the administrators field and click the{" "}
                                  <strong>X</strong> next to each one to remove
                                  them
                                </>
                              ),
                            },
                            {
                              step: 4,
                              title: "Save your changes",
                              description: (
                                <>
                                  Click <strong>OK</strong> to confirm and save
                                  your changes. The stale references will no
                                  longer appear after the next sync
                                </>
                              ),
                            },
                          ].map(({ step, title, description }) => (
                            <div
                              key={step}
                              className="d-flex align-items-center gap-3 p-2 rounded"
                              style={{
                                backgroundColor: "#fafafa",
                                border: "1px solid #f0e6c8",
                              }}
                            >
                              {/* Step number bubble */}
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold text-white"
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  minWidth: "28px",
                                  backgroundColor: "#e6a817",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {step}
                              </div>
                              {/* Step content */}
                              <div className="d-flex flex-column text-start">
                                <span
                                  className="fw-semibold"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  {title}
                                </span>
                                <span
                                  className="text-muted"
                                  style={{ fontSize: "0.82rem" }}
                                >
                                  {description}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="alert alert-info d-flex align-items-start gap-2 py-2 mb-0">
                          <span>💡</span>
                          <span style={{ fontSize: "0.82rem" }}>
                            <strong>Tip:</strong> Keep this page open in another
                            tab so you can refer to the account list while
                            following the steps above.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* end accordion */}
                <br></br>
              </div>
              {/* end card-body */}
            </div>
            {/* end card */}
          </div>
        )}
        {/* ── end deleted-account notice ── */}

        <div className="row">
          <div className="col">
            <br />
            <div
              style={{
                paddingLeft: "14px",
                paddingRight: "14px",
                display: props.currentOwners.length > 0 ? "grid" : "none",
              }}
            >
              <CommandBar
                items={_items}
                overflowButtonProps={overflowProps}
                ariaLabel="Inbox actions"
                primaryGroupAriaLabel="Email actions"
                farItemsGroupAriaLabel="More actions"
              />
              <hr style={{ color: "rgb(233, 233, 233)", margin: "0" }}></hr>
              <div
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                }}
              >
                <div className="col-sm-3"></div>
              </div>
              <table className="table" style={{ backgroundColor: "white" }}>
                <thead>
                  <tr style={{ textAlign: "justify" }}>
                    <th>
                      <input
                        type="checkbox"
                        style={{ width: "20px", height: "20px" }}
                        disabled={props.isAGS}
                        onChange={(event) => {
                          setAllCheckboxesByClass(
                            `chk-owners-${props.isM365 ? "M365" : "SPO"}-${props.isM365 ? props.resourceId : props.siteId}`,
                            event.target.checked as boolean,
                          );
                        }}
                      />
                    </th>
                    <th>Name</th>
                    <th>User Type</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {allOwners.map((owner: ownerObj, index) => {
                    return (
                      <tr key={index} style={{ textAlign: "justify" }}>
                        <td>
                          <input
                            type="checkbox"
                            style={{ width: "20px", height: "20px" }}
                            disabled={
                              props.isAGS ||
                              props.readOnlyOwners.includes(owner.email)
                            }
                            className={`chk-owners-${props.isM365 ? "M365" : "SPO"}-${props.isM365 ? props.resourceId : props.siteId}`}
                            onChange={(event) => {
                              handleOwnerCheckColumn(
                                owner.email,
                                event.target.checked,
                              );
                            }}
                          />
                        </td>
                        <td style={{ width: "40%" }}>
                          {!owner.isValid ? (
                            <Person
                              fallbackDetails={{
                                displayName: owner.email,
                                initials: "AD",
                              }}
                              personCardInteraction={PersonCardInteraction.none}
                              avatarType="photo"
                              avatarSize="small"
                              view={ViewType.oneline}
                            ></Person>
                          ) : (
                            <Person
                              fetchImage
                              personQuery={owner.email}
                              person-card="hover"
                              showPresence={true}
                              personCardInteraction={
                                PersonCardInteraction.hover
                              }
                              fallbackDetails={{
                                displayName: owner.email,
                                initials: "US",
                              }}
                              avatarType="photo"
                              avatarSize="small"
                              view={ViewType.oneline}
                            ></Person>
                          )}
                        </td>
                        <td style={{ width: "15%" }}>
                          <span className="badge rounded-pill text-bg-primary">
                            {props.isM365
                              ? "Owner"
                              : "Site Collection Administrator"}
                          </span>{" "}
                          <span
                            className={
                              owner.isValid
                                ? "badge rounded-pill text-bg-success"
                                : "badge rounded-pill generic-user-pill"
                            }
                          >
                            {owner.isValid ? "User" : "System/Generic"}
                          </span>
                        </td>
                        <td style={{ width: "45%" }}>
                          <Link href={owner.email}>{owner.email}</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <br />
      </div>
    </div>
  );
}
